import { useState } from 'react'
import { useUuiContext, UuiContexts } from '@epam/uui-core'
import { SidebarPanelHeader } from '../../../../components/SidebarPanel/components/SidebarPanelHeader'
import { SidebarPanelMain } from '../../../../components/SidebarPanel/components/SidebarPanelMain'
import { ReviewsList } from './components/ReviewList/ReviewList'
import { NewReviewForm } from '../NewReviewForm/NewReviewForm'
import { ReviewSummary } from './components/ReviewSummary/ReviewSummary'
import { ReviewTypeEnum } from '../../../../constants/enums'
import { showConfirmReviewDeleteModal } from '../../../../utils/modals/showConfirmReviewDeleteModal'
import {
  LocationReviewResponse,
  PointReviewResponse,
} from '../../../../constants/types'
import { useDeleteReviewMutation } from '../../../../store'
import { useAppSelector } from '../../../../hooks/redux/redux'

type ReviewsProps = {
  itemId: number
  itemRating: number
  itemName: string
  itemType?: string
  type: keyof typeof ReviewTypeEnum
  refetchItems: (id: number) => void
  refetchReviews: (id: number) => void
  reviews: PointReviewResponse[] | LocationReviewResponse[] | undefined
}

const config = {
  point: {
    requestIdName: 'pointId',
  },
  location: {
    requestIdName: 'locationId',
  },
}

export const Reviews = ({
  itemId,
  itemRating,
  itemName,
  itemType,
  refetchItems,
  type,
  reviews,
  refetchReviews,
}: ReviewsProps) => {
  const { userInfo: user } = useAppSelector((state) => state.users)
  const svc = useUuiContext<UuiContexts, any>()
  const [showReviewReadonly, toggleEditReview] = useState<number>(0)
  const [deleteReview] = useDeleteReviewMutation()

  const requestIdName = config[type].requestIdName

  const onDeleteReview = (reviewId: number) => {
    const deleteReviewHandler = () => deleteReview(reviewId).unwrap()

    showConfirmReviewDeleteModal(svc, deleteReviewHandler).then(() => {
      refetchItems(itemId)
      refetchReviews(itemId)
    })
  }

  const hasNotUserReview = !reviews?.some(
    (review) => review.creator.id === user?.id
  )

  return (
    <>
      {hasNotUserReview && <SidebarPanelHeader title="Add Review" />}

      {hasNotUserReview && (
        <SidebarPanelMain withBorderBottom>
          {user && (
            <NewReviewForm
              itemId={itemId}
              itemName={itemName}
              itemType={itemType}
              refetchReviews={refetchReviews}
              refetchItems={refetchItems}
              requestIdName={requestIdName}
            />
          )}
        </SidebarPanelMain>
      )}

      <SidebarPanelHeader title="Review summary" />
      <SidebarPanelMain withBorderBottom>
        <ReviewSummary itemRating={itemRating} />
      </SidebarPanelMain>

      <SidebarPanelHeader title="Reviews" />
      <SidebarPanelMain>
        <ReviewsList
          reviews={reviews}
          user={user}
          deleteReview={onDeleteReview}
          refetchReviews={refetchReviews}
          refetchItems={refetchItems}
          isToggle={showReviewReadonly}
          toggleEditReview={toggleEditReview}
        />
      </SidebarPanelMain>
    </>
  )
}
